import { createTitle } from './ui/title.js';
import { createCreate } from './ui/create.js';
import { createGate } from './ui/gate.js';
import { createSaveStore } from './sim/saveStore.js';
import { front } from './front.js';

/**
 * The front door (step 96): the title and the create screen, up the moment
 * this — a few kilobytes — has run, with the game loading behind them.
 *
 * The game used to build both fighters before it drew anything: three.js, the
 * default body, the clips, all of it, and only then the title. On a slow line
 * that was a blank page for twenty seconds. Now the order is the other way
 * round. This module needs nothing but the screens and a peek at the save,
 * and `game.js` — everything with a body in it — is asked for **at once** and
 * arrives while the player reads the title and picks a style.
 *
 * The one rule: nothing starts a career, and so nothing reaches a fight,
 * before the game is ready. "Continue" and "start climbing" go through `when`,
 * which carries the choice out straight away if the game has landed, and
 * otherwise puts up the warming-up screen and carries it out the moment it
 * does. If the game cannot load at all, the warming-up screen says so and
 * offers a reload; the career in `localStorage` is untouched either way.
 */

// The splash in `index.html` was the first paint, before any script. The
// real title replaces it.
document.getElementById('splash')?.remove();

/** A save store with no career behind it: enough to describe what is saved. */
const peek = createSaveStore({});

let game = null;
let failure = null;
const loading = import('./game.js').then(
  (module) => {
    game = module;
    return module;
  },
  (error) => {
    failure = error;
    console.error(`[boot] the game did not load: ${error?.message ?? error}`);
    throw error;
  }
);
loading.catch(() => {});

const gate = createGate();

/** Does `action(game)` now if the game is here, or as soon as it is. */
function when(action, what) {
  if (game) return action(game);
  if (failure) return gate.fail(failure.message);
  gate.wait(what);
  loading.then(
    (module) => {
      gate.hide();
      action(module);
    },
    (error) => gate.fail(error?.message)
  );
  return null;
}

const title = createTitle({
  store: peek,
  onContinue: () => when((g) => g.continueCareer(), 'your fighter is on the way'),
  // A new career is a *fighter* first. The screen hands back a name and a list
  // of style ids and decides nothing else.
  onNew: () => create.show(),
});

const create = createCreate({
  onBack: () => title.show(),
  onDone: (made) => when((g) => g.newCareer(made), `${made.name} is ready; the ring is still being built`),
});

front.title = title;
front.create = create;
front.gate = gate;

title.show();

/** For a harness: whether the game has loaded, and the promise that says so. */
window.__FRONT__ = {
  get ready() {
    return Boolean(game);
  },
  loading,
  gate,
};
